import { useState, useContext, useEffect } from "react"

import { DateTime } from "luxon"

//Task modules
import {
  taskApiCreateTasksPOST,
  taskApiUpdateTaskPUT,
  taskApiDELETE,
} from "../TasksApi"

//primeReact
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { Calendar } from "primereact/calendar"
import { Chips } from "primereact/chips"
import { MultiSelect } from "primereact/multiselect"
import { ProgressSpinner } from "primereact/progressspinner"
import { Editor } from "primereact/editor"

//Formik
import { Formik, Form, useField } from "formik"
import * as Yup from "yup"
import FormikTextInput from "../../../helpers/formikInputs/FormikTextInput"
import FormikCalendar from "../../../helpers/formikInputs/FormikCalendar"

// Context
import { UserContext } from "../../../context/userContext"

//styles
import styles from "../../../styles/Form-Inputs.module.css"
import taskCreateStyles from "../../../styles/tasks/TasksFormCreate.module.css"

//Helpers
import { LogError } from "../../../helpers/logger"
import TaskCreateFormNotesInput from "./TaskCreateFormNotesInput"
import TaskCreateFormAssignInput from "./TaskCreateFormAssignInput"

export default function TaskCreateForm(props) {
  const userCtx = useContext(UserContext)

  const [userOptions, setUserOptions] = useState([{}])

  const [task, setTask] = useState({
    id: "",
    title: "",
    description: "",
    assignedToUserIds: [],
    dueOn: DateTime.local().toJSDate(),
  })

  useEffect(() => {
    if (!!props.task) {
      let usr = [props.getUser(props.task.assignedToUserId)]

      if (usr[0] === null) {
        usr = []
      }

      setTask({
        id: props.task.id,
        groupId: props.task.groupId,
        createdByUserId: props.task.createdByUserId,
        createdAt: props.task.createdAt,
        title: props.task.title,
        description: props.task.description,
        assignedToUserIds: usr,
        dueOn: DateTime.fromISO(props.task.dueOn, {
          zone: "utc",
        })
          .toLocal()
          .toJSDate(),
      })
    }
  }, [props, props.task, userCtx.session.user.staff_id])

  useEffect(() => {
    const groups = [...new Set(props.users.map((x) => x.userRole))]
    const options = groups.map((x) => {
      return {
        name: x,
        items: props.users.filter((y) => y.userRole === x),
      }
    })
    setUserOptions(options)
  }, [props.users])

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Please enter a title"),
    assignedToUserIds: Yup.array()
      .min(1, "Please assign at least one user")
      .required("Please assign at least one user"),
  })

  async function saveChanges(values) {
    if (values.id) {
      //Edit
      try {
        if (values.assignedToUserIds.length > 1) {
          throw new Error("Multiple assignments not supported for edit")
        }
        const dueDate = DateTime.fromJSDate(values.dueOn).toUTC().toISO()
        await taskApiUpdateTaskPUT(userCtx.tokens.getTaskToken(), {
          id: values.id,
          groupId: userCtx.session.user.organization_id,
          createdByUserId: userCtx.session.user.staff_id,
          createdAt: values.createdAt,
          assignedToUserId: values.assignedToUserIds[0].id,
          assignedToUserEmail: values.assignedToUserIds[0].email,
          title: values.title,
          description: values.description,
          dueOn: dueDate,
        })

        props.onSetShowForm(false)
        props.onSetTask(null)
        props.onRefreshTasks(new Date().getTime())
        return true
      } catch (error) {
        LogError("Unable to update task", error)
        props.onShowError(
          "Unable to update task, please see console for errors",
        )
        return false
      }
    } else {
      //Create
      try {
        await taskApiCreateTasksPOST(userCtx.tokens.getTaskToken(), {
          groupId: userCtx.session.user.organization_id,
          createdByUserId: userCtx.session.user.staff_id,
          assignedToUsers: values.assignedToUserIds.map((user) => {
            return {
              assignedToUserId: user.id,
              assignedToUserEmail: user.email,
            }
          }),
          title: values.title,
          description: values.description,
          dueOn: DateTime.fromJSDate(values.dueOn).toUTC().toISO(),
        })

        props.onSetShowForm(false)
        props.onSetTask(null)
        props.onRefreshTasks(new Date().getTime())
        return true
      } catch (error) {
        LogError("Unable to update task", error)
        props.onShowError(
          "Unable to create task, please see console for errors",
        )
        return false
      }
    }
  }

  async function deleteTask(taskId) {
    try {
      await taskApiDELETE(userCtx.tokens.getTaskToken(), taskId)

      props.onRefreshTasks(new Date().getTime())
      props.onSetShowForm(false)
      props.onSetTask(null)
    } catch (error) {
      LogError("Unable to delete task", error)
      props.onShowError("Unable to delete task, please see console for errors")
    }
  }

  return (
    <Formik
      initialValues={task}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => {
        return saveChanges(values)
      }}
    >
      {({ handleSubmit, isValid, isSubmitting, dirty, values, errors }) =>
        isSubmitting ? (
          <div style={{ textAlign: "center" }}>
            <ProgressSpinner />
          </div>
        ) : (
          <Form style={{ width: "100%" }}>
            <div style={{ width: "100%", transition: "all .5s linear" }}>
              <FormikTextInput name={"title"} label={"Title"} />
              <TaskCreateFormNotesInput />
              <TaskCreateFormAssignInput
                task={task}
                getUser={props.getUser}
                userOptions={userOptions}
                users={props.users}
              />
              <FormikCalendar
                name={"dueOn"}
                label={"Due date and time"}
                minDate={new Date()}
                hourFormat="12"
                showTime={true}
              />
              <div style={{ marginTop: "10px" }}>
                {values?.id ? (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => {
                      deleteTask(values.id)
                    }}
                  >
                    Delete
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn"
                  style={{ float: "right" }}
                  onClick={() => {
                    props.onSetShowForm(false)
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn"
                  style={{ float: "right", marginRight: 10 }}
                  onClick={handleSubmit}
                >
                  Save
                </button>
              </div>
            </div>
          </Form>
        )
      }
    </Formik>
  )
}
